import { updateConfigByKey } from '@app/config-builder/common/utils';
import { configAtom, ConfigType, store } from '@app/config-builder/store';
import { buildGradient } from '@app/config-builder/tabs/colors/GradientColorItem';
import { get } from 'lodash';
import { defaultTranslations } from '@/modules/common';
import { PageType } from '@/modules/models';


export const API_URL_DEV = 'https://dev-saywhatt-frj5cmaowa-uk.a.run.app';
export const API_URL_PROD = 'https://saywhatt-frj5cmaowa-uk.a.run.app';
export const DIRECTUS_URL = 'https://saywhatt-admin-frj5cmaowa-uk.a.run.app';
export const DIRECTUS_TOKEN = '0aJYWpkhQFLudxHp40ptjbXf-t37pYnv';

export const PAGE_TYPES = {
  PRODUCT: 'PRODUCT',
  COLLECTION: 'COLLECTION',
  DEFAULT: 'DEFAULT',
  HOME: 'HOME',
  CART: 'CART',
};

export enum TabType {
  COLORS = 'Colors',
  SIZES = 'Sizes & Positioning',
  TEXTS = 'Texts',
  SETTINGS = 'Settings',
  OVERRIDES = 'Overrides',
}

export const TABS = [
  TabType.COLORS,
  TabType.SIZES,
  TabType.TEXTS,
  TabType.SETTINGS,
  TabType.OVERRIDES
];

export const DEFAULT_CONFIG: ConfigType = {
  pageType: PageType.HOME,
  isAlwaysOpenWSConnection: false,
  handleIframeMouseEvents: true,
  widget: {
    initTooltip: {
      duration: 5000,
      closeBtn: true,
    },
    stuckTooltip: {
      delay: 15000,
      duration: 5000,
    },
    productsTooltip: {
      products: 3,
      duration: 5000,
    },
    crossSaleTooltip: {
      delay: 2500,
      duration: 5000,
    },
  },
  translations: {
    ...defaultTranslations,
  },
  theme: {
    fontFamily: "'Benton Sans', Arial",
    widget: {
      wrapper: {
        desktop: {
          right: '50px',
          bottom: '40px',
          height: '60px',
        },
        mobile: {
          right: '50px',
          bottom: '40px',
          height: '60px',
        },
      },
    },
  },
};

export const TRANSLATIONS_PARAMS_MAP = {
  wTitle: 'widget.title',
  cTitle: 'title',
  ph: 'inputPlaceholder',
  initPopup: 'widget.initPopup',
  stuckPopup: 'widget.stuckPopup',
  viewedProductsPopup: 'widget.viewedProductsPopup',
  crossSalePopup: 'widget.crossSalePopup',
  checkItOut: 'checkItOut',
  addToCart: 'addToCartButton',
  quant: 'quantity',
  subscriptions: 'subscriptions',
};

export const TRANSLATIONS_LABELS_MAP = {
  wTitle: 'Widget Title',
  cTitle: 'Chat Title',
  ph: 'Input Placeholder',
  initPopup: 'Init Popup Text',
  stuckPopup: 'Stuck Popup Text',
  viewedProductsPopup: 'Viewed Products Popup Text',
  crossSalePopup: 'Cross Sale Popup Text',
  checkItOut: 'Check It Out Link',
  addToCart: 'Add To Cart Button',
  quant: 'Quantity Text',
  subscriptions: 'Subscriptions Block',
};

export const COLORS_PARAMS_MAP = {
  light: 'theme.colors.light',
  dark: 'theme.colors.dark',
};

export const getDefaultGradient = (endColor?: string) => {
  const config = store.get(configAtom);
  let color = get(config, COLORS_PARAMS_MAP.light, '#FFFFFF');
  if (endColor && endColor !== '#FFFFFF') {
    color = endColor;
  }
  return buildGradient('#FFFFFF', color);
}

export const ADVANCED_COLORS_PARAMS_MAP: Record<
  string,
  {
    path: string;
    defaultPath?: string;
    defaultValue?: string;
    supportsGradient?: boolean;
    callback?: (newValue: string | undefined) => void;
  }
> = {
  wBg: {
    path: 'theme.widget.background.title.background',
    supportsGradient: true,
    callback: (newValue) => {
      updateConfigByKey('theme.widget.background.tooltip.background', newValue);
    },
  },
  wInnerBg: {
    path: 'theme.widget.content.styles.background',
    supportsGradient: true,
  },
  wTextColor: {
    path: 'theme.widget.content.styles.color',
    defaultValue: '#000000',
  },
  wStars: {
    path: 'theme.widget.star.fill',
    defaultPath: COLORS_PARAMS_MAP.dark,
  },
  wBorderColor: {
    path: 'theme.widget.content.styles.borderColor',
    defaultPath: COLORS_PARAMS_MAP.dark,
  },
  cHeaderText: {
    path: 'theme.header.styles.color',
    defaultValue: '#000000',
  },
  cHeaderBg: {
    path: 'theme.header.styles.background',
    defaultValue: 'transparent',
  },
  cStars: {
    path: 'theme.header.styles.& > div.svg.path.fill',
    defaultValue: '#000000',
  },
  cCloseBtn: {
    path: 'theme.header.closeButton.& path.stroke',
    defaultValue: '#000000',
  },
  cBg: {
    path: 'theme.container.styles.background',
    supportsGradient: true,
  },
  userBg: {
    path: 'theme.bubble.user.background',
    defaultPath: COLORS_PARAMS_MAP.light,
  },
  userColor: {
    path: 'theme.messageText.user.color',
    defaultValue: '#000000',
  },
  mlBg: {
    path: 'theme.bubble.ml.background',
    defaultValue: '#FCFAF8',
  },
  mlColor: {
    path: 'theme.messageText.ml.color',
    defaultValue: '#000000',
  },
  addToCartBtnBg: {
    path: `theme.messageText.ml['.product-button'].backgroundColor`,
    defaultValue: '#000000',
  },
  addToCartBtnText: {
    path: `theme.messageText.ml['.product-button'].color`,
    defaultPath: COLORS_PARAMS_MAP.light,
  },
  sendBtnIcon: {
    path: 'theme.sendButton.styles.svg.fill',
    defaultPath: COLORS_PARAMS_MAP.light,
  },
  sendBtnBg: {
    path: 'theme.sendButton.styles.&:enabled.backgroundColor',
    defaultValue: '#000000',
  },
  sendBtnDisabledIcon: {
    path: 'theme.sendButton.styles.&:disabled.svg.fill',
    defaultPath: COLORS_PARAMS_MAP.light,
  },
  sendBtnDisabledBg: {
    path: 'theme.sendButton.styles.&:disabled.backgroundColor',
    defaultValue: '#6c6c6c',
  },
  inputBg: {
    path: 'theme.input.styles.backgroundColor',
    defaultValue: '#f2f2f2',
    callback: (newValue) => {
      updateConfigByKey('theme.uploadButton.styles.backgroundColor', newValue);
    },
  },
};

export const COLORS_LABELS_MAP = {
  light: 'Light Color',
  dark: 'Dark Color',
};

export const ADVANCED_COLORS_LABELS_MAP = {
  wBg: 'Widget Background',
  wInnerBg: 'Widget Inner Background',
  wTextColor: 'Widget Text Color',
  wStars: 'Widget Stars',
  wBorderColor: 'Widget Border Color',
  cStars: 'Chat Stars',
  cHeaderText: 'Chat Header Text',
  cHeaderBg: 'Chat Header Background',
  cCloseBtn: 'Chat Close Button',
  cBg: 'Chat Background',
  userBg: 'User Message Bubble',
  userColor: 'User Message Text',
  mlBg: 'ML Message Bubble',
  mlColor: 'ML Message Text',
  addToCartBtnBg: 'Add to Cart Button Background',
  addToCartBtnText: 'Add to Cart Button Text',
  sendBtnIcon: 'Send Button Icon',
  sendBtnBg: 'Send Button Background',
  sendBtnDisabledIcon: 'Send Button Icon (Disabled)',
  sendBtnDisabledBg: 'Send Button Background (Disabled)',
  inputBg: 'Input Background',
};

export const SETTINGS_PARAMS_MAP = {
  cdb: 'theme.container.desktop.bottom',
  cdr: 'theme.container.desktop.right',
  cmb: 'theme.container.mobile.bottom',
  cmr: 'theme.container.mobile.right',
  wdb: 'theme.widget.wrapper.desktop.bottom',
  wdr: 'theme.widget.wrapper.desktop.right',
  wmb: 'theme.widget.wrapper.mobile.bottom',
  wmr: 'theme.widget.wrapper.mobile.right',
  wdh: 'theme.widget.wrapper.desktop.height',
  wmh: 'theme.widget.wrapper.mobile.height',
  wdz: 'theme.widget.wrapper.desktop.zIndex',
  wmz: 'theme.widget.wrapper.mobile.zIndex',
  cdz: 'theme.container.desktop.zIndex',
  cmz: 'theme.container.mobile.zIndex',
  cdmh: 'theme.container.desktop.maxHeight',
  cmh: 'theme.container.mobile.height',
  faviconUrl: 'previewSettings.faviconUrl',
  pageTitle: 'previewSettings.pageTitle',
  iframeUrl: 'previewSettings.iframeUrl',
  fontFamily: 'theme.fontFamily',
  isUploadBtnActive: 'isUploadBtnActive',
  isRtl: 'theme.isRtl',
  isTestModeAvailable: 'isTestModeAvailable',
};

export const SETTINGS_LABELS_MAP = {
  cdb: 'Desktop Bottom',
  cdr: 'Desktop Right',
  cmb: 'Mobile Bottom',
  cmr: 'Mobile Right',
  wdb: 'Desktop Bottom',
  wdr: 'Desktop Right',
  wmb: 'Mobile Bottom',
  wmr: 'Mobile Right',
  wdh: 'Desktop Height',
  wmh: 'Mobile Height',
  wdz: 'Desktop Z-Index',
  wmz: 'Mobile Z-Index',
  cdmh: 'Desktop Max Height',
  cmh: 'Mobile Height',
  cdz: 'Desktop Z-Index',
  cmz: 'Mobile Z-Index',
  faviconUrl: 'Favicon URL',
  pageTitle: 'Page Title',
  iframeUrl: 'Iframe URL',
  fontFamily: 'Font Family',
  isUploadBtnActive: 'Enable Attachments',
  isRtl: 'Right to Left',
  isTestModeAvailable: 'Test Mode Available',
};

export const PRODUCT_CARD_SETTINGS_MAP = {
  hV: 'productCard.hideVariants',
  hATCB: 'productCard.hideAddToCartButton',
  hCIO: 'productCard.hideCheckItOut',
};

export const PRODUCT_CARD_LABELS_MAP = {
  hV: 'Hide Variants',
  hATCB: 'Hide Add To Cart Button',
  hCIO: 'Hide Check It Out',
};

export const PROACTIVE_CHAT_SETTINGS_MAP = {
  delay: 'semiOpenConfig.delay',
  pageTypes: 'semiOpenConfig.pageTypes',
};

export const PROACTIVE_CHAT_LABELS_MAP = {
  delay: 'Delay (sec)',
  pageTypes: 'Page Types',
};
